import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useManualImport } from '@/pages/purchases/manualImport/useManualImport';
import {
    clearPendingImport,
    peekPendingImport,
} from '@/pages/purchases/components/excelImport/pendingFileImport';
import type { DoubtfulRow } from '@/pages/purchases/components/excelImport/excelImportMeta';
import { PATH } from '@/router/PATH';
import { makeFileImportDraft } from './fileImportMeta';

/**
 * Drives /purchases/file-import. Consumes the one-shot analysis result the
 * Excel-import modal stored, seeds the shared manual-import form with it
 * (method 'excel'), and owns the doubtful-rows review state: Promote appends
 * the row's best-effort suggestion as an editable line, Dismiss drops it.
 *
 * A direct visit / refresh finds no pending result → redirect to /purchases
 * (`isReady` stays false so the container renders nothing meanwhile).
 */
export const useFileImport = () => {
    const navigate = useNavigate();
    // Peek (pure) in the initializer — StrictMode double-invokes it — and
    // clear from the effect, so a refresh/revisit still finds nothing and
    // redirects while the first mount keeps its data.
    const [pending] = React.useState(() => peekPendingImport());

    React.useEffect(() => {
        clearPendingImport();
        if (!pending) {
            navigate(PATH.PURCHASES.ROOT, { replace: true });
        }
    }, [navigate, pending]);

    const onImported = React.useCallback(
        () => navigate(PATH.PURCHASES.ROOT),
        [navigate],
    );

    const form = useManualImport(
        pending ? makeFileImportDraft(pending) : undefined,
        { method: 'excel', onImported },
    );

    const [doubtfulRows, setDoubtfulRows] = React.useState<DoubtfulRow[]>(
        () => pending?.doubtful ?? [],
    );

    const onPromoteDoubtful = React.useCallback(
        (id: string) => {
            // Side effect outside the updater — React may invoke updaters
            // twice (StrictMode), which would double-append the line.
            const row = doubtfulRows.find((r) => r.id === id);
            if (!row) return;
            form.onAddPreparedLine(row.suggestion);
            setDoubtfulRows((rows) => rows.filter((r) => r.id !== id));
        },
        [doubtfulRows, form],
    );

    const onDismissDoubtful = React.useCallback(
        (id: string) =>
            setDoubtfulRows((rows) => rows.filter((r) => r.id !== id)),
        [],
    );

    return {
        isReady: pending !== null,
        form,
        doubtfulRows,
        onPromoteDoubtful,
        onDismissDoubtful,
    } as const;
};

/** Everything the file-import view tree needs. */
export type FileImportForm = ReturnType<typeof useFileImport>;
